import { useEffect, useId, useRef, useState } from "react";

type InfoTooltipProps = {
  message: string;
  label?: string;
  placement?: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center";
  className?: string;
  panelClassName?: string;
  panelPositionClassName?: string;
};

const placementClassName: Record<NonNullable<InfoTooltipProps["placement"]>, string> = {
  "top-left": "sm:bottom-full sm:left-0 sm:mb-2",
  "top-center": "sm:bottom-full sm:left-1/2 sm:mb-2 sm:-translate-x-1/2",
  "top-right": "sm:bottom-full sm:right-0 sm:mb-2",
  "bottom-left": "sm:top-full sm:left-0 sm:mt-2",
  "bottom-center": "sm:top-full sm:left-1/2 sm:mt-2 sm:-translate-x-1/2",
};

const InfoTooltip = ({
  message,
  label = "More info",
  placement = "top-center",
  className = "",
  panelClassName = "",
  panelPositionClassName,
}: InfoTooltipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipId = useId();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const positionClassName = panelPositionClassName ?? placementClassName[placement];

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={`relative inline-flex shrink-0 ${className}`}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? tooltipId : undefined}
        onClick={() => setIsOpen((current) => !current)}
        onFocus={() => setIsOpen(true)}
        onBlur={(event) => {
          if (!containerRef.current?.contains(event.relatedTarget as Node | null)) {
            setIsOpen(false);
          }
        }}
        className="-m-2 inline-flex h-9 w-9 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-myteengig-primary/35 sm:h-8 sm:w-8"
      >
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-myteengig-border bg-white align-middle text-[11px] font-bold leading-none text-myteengig-text-muted">
          i
        </span>
      </button>
      {isOpen ? (
	        <div
	          id={tooltipId}
	          className={`absolute z-[70] w-64 rounded-2xl border border-myteengig-border bg-myteengig-background px-3 py-2 text-left text-xs font-medium text-myteengig-text-primary shadow-lg max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:top-5 max-sm:w-auto max-sm:translate-x-0 ${positionClassName} ${panelClassName}`}
	          role="tooltip"
	        >
          {message}
        </div>
      ) : null}
    </div>
  );
};

export default InfoTooltip;
